/*
 * ARFORTI Customer Account API helper (Public client + PKCE).
 * Reads config from window.__ARFORTI_CAA__ = { clientId, redirectUri, logoutUri }.
 * Exposes window.ARFORTI_CAA = { login, handleCallback, isLoggedIn, query, logout, discover }.
 * Reusable by the waitlist page and the order-progress page.
 * Docs: https://shopify.dev/docs/api/customer/latest (Authentication / PKCE).
 */
(function () {
  var cfg = window.__ARFORTI_CAA__ || {};
  // localStorage (not sessionStorage) so a returning customer stays remembered across visits/tabs:
  // the refresh_token persists → the waitlist/order pages auto-authenticate silently (no re-login,
  // no sign-in screen) and just refresh the data on load. Cleared on logout.
  var SS = window.localStorage;
  var K = { token: 'caa_token', exp: 'caa_token_exp', refresh: 'caa_refresh', idt: 'caa_id_token', verifier: 'caa_verifier', state: 'caa_state', ret: 'caa_return' };

  function b64url(buf) {
    var bytes = new Uint8Array(buf), s = '';
    for (var i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
    return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  function randUrl(n) {
    var a = new Uint8Array(n);
    crypto.getRandomValues(a);
    return b64url(a.buffer);
  }
  function sha256(str) {
    return crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  }

  var _oidc = null, _caa = null;
  function discover() {
    var pOidc = _oidc ? Promise.resolve(_oidc)
      : fetch('/.well-known/openid-configuration').then(function (r) { return r.json(); }).then(function (j) { _oidc = j; return j; });
    var pCaa = _caa ? Promise.resolve(_caa)
      : fetch('/.well-known/customer-account-api').then(function (r) { return r.json(); }).then(function (j) { _caa = j; return j; });
    return Promise.all([pOidc, pCaa]).then(function (a) { return { oidc: a[0], caa: a[1] }; });
  }

  function buildAuthUrl(d) {
    var verifier = randUrl(32);
    return sha256(verifier).then(function (hash) {
      var challenge = b64url(hash);
      var state = randUrl(16), nonce = randUrl(16);
      SS.setItem(K.verifier, verifier);
      SS.setItem(K.state, state);
      SS.setItem(K.ret, location.pathname + location.search);
      var u = new URL(d.oidc.authorization_endpoint);
      u.searchParams.set('scope', 'openid email customer-account-api:full');
      u.searchParams.set('client_id', cfg.clientId);
      u.searchParams.set('response_type', 'code');
      u.searchParams.set('redirect_uri', cfg.redirectUri);
      u.searchParams.set('state', state);
      u.searchParams.set('nonce', nonce);
      u.searchParams.set('code_challenge', challenge);
      u.searchParams.set('code_challenge_method', 'S256');
      return u.toString();
    });
  }

  // Full-page redirect to the Shopify login (returns to redirect_uri?code).
  function login() {
    return discover().then(function (d) {
      return buildAuthUrl(d).then(function (url) { location.href = url; });
    });
  }

  function cleanUrl() {
    try { history.replaceState({}, '', location.pathname); } catch (e) {}
  }

  // If the URL has ?code & ?state from an OAuth redirect, exchange it for a token.
  function handleCallback() {
    var p = new URLSearchParams(location.search);
    var code = p.get('code'), state = p.get('state');
    if (!code) return Promise.resolve(false);
    if (!state || state !== SS.getItem(K.state)) { cleanUrl(); return Promise.resolve(false); }
    var verifier = SS.getItem(K.verifier);
    return discover().then(function (d) {
      var body = new URLSearchParams();
      body.set('grant_type', 'authorization_code');
      body.set('client_id', cfg.clientId);
      body.set('redirect_uri', cfg.redirectUri);
      body.set('code', code);
      body.set('code_verifier', verifier);
      return fetch(d.oidc.token_endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
        body: body
      });
    }).then(function (res) {
      if (!res.ok) { cleanUrl(); return false; }
      return res.json().then(function (t) {
        SS.setItem(K.token, t.access_token);
        SS.setItem(K.exp, String(Date.now() + (t.expires_in * 1000) - 60000));
        if (t.refresh_token) SS.setItem(K.refresh, t.refresh_token);
        if (t.id_token) SS.setItem(K.idt, t.id_token);
        SS.removeItem(K.verifier);
        SS.removeItem(K.state);
        cleanUrl();
        return true;
      });
    }).catch(function () { cleanUrl(); return false; });
  }

  function tokenValid() {
    var t = SS.getItem(K.token), e = parseInt(SS.getItem(K.exp), 10);
    return !!t && Date.now() < e;
  }

  function clearTokens() {
    SS.removeItem(K.token); SS.removeItem(K.exp); SS.removeItem(K.refresh); SS.removeItem(K.idt);
  }

  // Silently renew the access token using the refresh_token (public client → no secret, per docs).
  function refresh() {
    var rt = SS.getItem(K.refresh);
    if (!rt) return Promise.resolve(false);
    return discover().then(function (d) {
      var body = new URLSearchParams();
      body.set('grant_type', 'refresh_token');
      body.set('client_id', cfg.clientId);
      body.set('refresh_token', rt);
      return fetch(d.oidc.token_endpoint, { method: 'POST', headers: { 'content-type': 'application/x-www-form-urlencoded' }, body: body });
    }).then(function (res) {
      if (!res.ok) { clearTokens(); return false; }
      return res.json().then(function (t) {
        SS.setItem(K.token, t.access_token);
        SS.setItem(K.exp, String(Date.now() + (t.expires_in * 1000) - 60000));
        if (t.refresh_token) SS.setItem(K.refresh, t.refresh_token);
        if (t.id_token) SS.setItem(K.idt, t.id_token);
        return true;
      });
    }).catch(function () { return false; });
  }

  // Resolve true with a usable access token, refreshing first if it has expired.
  function ensureToken() {
    if (tokenValid()) return Promise.resolve(true);
    return refresh();
  }

  // Signed in if the access token is valid OR a refresh_token is available to renew it.
  function isLoggedIn() {
    return tokenValid() || !!SS.getItem(K.refresh);
  }

  function query(gql, variables) {
    return ensureToken().then(function (ok) {
      if (!ok) return { errors: [{ message: 'Not authenticated' }] };
      return discover().then(function (d) {
        return fetch(d.caa.graphql_api, {
          method: 'POST',
          headers: { 'content-type': 'application/json', 'Authorization': SS.getItem(K.token) },
          body: JSON.stringify({ query: gql, variables: variables || {} })
        }).then(function (r) { return r.json(); });
      });
    });
  }

  function logout() {
    var idt = SS.getItem(K.idt);
    clearTokens();
    if (!idt) { location.href = cfg.logoutUri || '/'; return Promise.resolve(); }
    return discover().then(function (d) {
      var end = d.oidc && d.oidc.end_session_endpoint;
      if (!end) { location.href = cfg.logoutUri || '/'; return; }
      var u = new URL(end);
      u.searchParams.set('id_token_hint', idt);
      u.searchParams.set('post_logout_redirect_uri', cfg.logoutUri || location.origin + '/');
      location.href = u.toString();
    }).catch(function () { location.href = cfg.logoutUri || '/'; });
  }

  // A sign-in click paints the button busy (is-pulse: transparent text, no pointer events) and
  // then hands off to a full-page OAuth redirect. Press Back and the page comes out of the
  // bfcache exactly as it was left — busy paint included, but with no redirect still in flight,
  // so the button reads as stuck loading and cannot be clicked again. Nothing else fires on a
  // bfcache restore, so clear it here; `persisted` keeps a real load (where a pulse may have
  // been set legitimately during the ?code= exchange) untouched.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    var stuck = document.querySelectorAll('.is-pulse, .is-loading, .waitlist--authing');
    for (var i = 0; i < stuck.length; i++) {
      stuck[i].classList.remove('is-pulse', 'is-loading', 'waitlist--authing');
    }
  });

  window.ARFORTI_CAA = { login: login, handleCallback: handleCallback, isLoggedIn: isLoggedIn, query: query, logout: logout, discover: discover };
  document.dispatchEvent(new Event('caa:ready'));
})();
